import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, CalendarDays, User } from "lucide-react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { ASSETS } from "@/constants/site";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError("Post não encontrado"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto p-8 text-slate-500">Carregando...</div>;
  if (error || !post) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <p className="text-slate-600">{error || "Post não encontrado"}</p>
        <Link to="/blog" className="text-brand-600 mt-4 inline-block">← Voltar ao blog</Link>
      </div>
    );
  }

  return (
    <>
      <SEO title={post.title} description={post.excerpt} path={`/blog/${post.slug}`} />

      <article data-testid={TESTIDS.blogPost} className="bg-white pb-20">
        <div className="w-full aspect-[21/9] max-h-[520px] overflow-hidden bg-slate-100">
          <img src={post.cover_image || ASSETS.blogGeneric} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8 sm:p-12">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 mb-6">
              <ArrowLeft className="w-4 h-4" /> Voltar ao blog
            </Link>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">{post.title}</h1>
            <div className="mt-4 flex items-center gap-4 text-sm text-slate-500 flex-wrap">
              <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{new Date(post.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}</span>
              <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" />{post.author}</span>
              {post.tags?.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {post.tags.map((t) => (
                    <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 border border-brand-100">{t}</span>
                  ))}
                </div>
              )}
            </div>

            <div className="prose-visiovet mt-8">
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </div>
          </div>
        </div>
      </article>
    </>
  );
}
